import { useQueryClient } from '@tanstack/react-query'
import uniq from 'lodash/uniq'
import Push from 'push.js'
import { useCallback, useEffect } from 'react'
import { useRouteMatch } from 'react-router'

import { APPLI_URL } from 'src/constants/constants'
import { useUser, useUserType } from 'src/contexts/userContext'
import { ORDERS_QUERY_KEY } from 'src/queries/buyer/orders'
import { updateOrderItemInOrderCache } from 'src/queries/helpers/updateOrderItemInOrderCache'
import { DISPATCH_ORDER_ITEMS_QUERY_KEY, PURCHASE_ORDERS_QUERY_KEY } from 'src/queries/shared'
import { BuyerOrderItem, SellerOrderItem } from 'src/types'
import getPusher, {
  ORDER_ITEM_UPDATED,
  PusherPrivateChannelType,
  PusherUnsubscribeFunction,
} from 'src/utils/pusher'

import { useOptionFlags } from './useOptionFlags'

const useOrganisationPusherSubscriptions = () => {
  const user = useUser()
  const { optionFlagCanUseCrossDockingPurchaseOrders } = useOptionFlags()
  const { isBuyer } = useUserType()
  const queryClient = useQueryClient()
  const pusherPrivateChannel = getPusher().privateChannel
  const matchPreparationPage = useRouteMatch({ exact: true, path: '/:locale/seller/orders/:id' })

  const handlePusherOrderItemUpdated = useCallback(
    (payload: BuyerOrderItem | SellerOrderItem) => {
      // Do not update order item from pusher on preparation page
      if (!matchPreparationPage) {
        updateOrderItemInOrderCache({
          newOrderItem: payload,
          queryClient,
          queryKey: ORDERS_QUERY_KEY,
        })
      }

      queryClient.invalidateQueries({
        queryKey: [DISPATCH_ORDER_ITEMS_QUERY_KEY, { order: payload.orderId }],
      })

      if (optionFlagCanUseCrossDockingPurchaseOrders) {
        updateOrderItemInOrderCache({
          newOrderItem: payload,
          queryClient,
          queryKey: PURCHASE_ORDERS_QUERY_KEY,
        })
      }

      if (Push.Permission.has()) {
        Push.create(gettext('Klarys - Order Item Updated'), {
          body: interpolate(
            gettext('An order item in the order n°%(orderId)s has been updated'),
            {
              orderId: payload.orderId.toString(),
            },
            true
          ),
          icon: '/favicon.ico',
          link: isBuyer ? APPLI_URL.BUYER.ORDERS.MY_ORDERS : APPLI_URL.SELLER.ORDERS.BASE,
        })
      }
    },
    [queryClient]
  )

  useEffect(() => {
    let unSubscribePrivateOrganisationChannels: PusherUnsubscribeFunction[] | undefined
    if (user) {
      const organisationPusherUuids = uniq(
        user.memberships.map(membership => membership.organisationPusherUuid)
      )

      unSubscribePrivateOrganisationChannels = organisationPusherUuids.map(organisationPusherUuid =>
        pusherPrivateChannel(
          organisationPusherUuid,
          PusherPrivateChannelType.ORGANISATION
        ).subscribe({
          [ORDER_ITEM_UPDATED]: handlePusherOrderItemUpdated,
        })
      )
    }

    return () =>
      unSubscribePrivateOrganisationChannels?.forEach(unsubcribeChannel => unsubcribeChannel())
  }, [JSON.stringify(user), handlePusherOrderItemUpdated])
}

export default useOrganisationPusherSubscriptions
